import axios from "axios";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { passKeyAddorDeleteFail, passKeyFetchFail, trustedDevicesFetchFail } from "../../constants/static";
import { devicesUrl } from "../../constants/urls";
import { StateContext } from "../../context/stateContext";
import { RemoveDeviceDialog } from "../RemoveDeviceDialog/RemoveDeviceDialog";
import { DeviceCard } from "../TrustedDevices/DeviceCard";
import { DevicesStepResponse, PassKey } from "../TrustedDevices/TrustedDevice.type";

export const PassKeyListAndAdd = ({ closeAlert }: { closeAlert: any }) => {
  const [passKeyStep1Response, setPassKeyStep1Response] = useState<DevicesStepResponse>();
  const [passKey, setPassKey] = useState<PassKey[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    cidmUrl,
    accessToken,
    passKeyStep2Response,
    setPassKeyStep2Response,
    setAddPassKeySuccess,
    setAddedPassKeyMessage,
    languageCode,
    setIsError,
    setPassKeyCount,
    setRemoveDeviceSuccess,
    setLastRemovedDeviceMessage,
  } = useContext(StateContext);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deviceToRemove, setDeviceToRemove] = useState<string>("");
  const [dialogDeviceMessage, setDialogDeviceMessage] = useState("");
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [passKeyDeleteResponse, setPassKeyDeleteResponse] = useState<DevicesStepResponse>();
  const [passKeyDeleteStep2Response, setPassKeyDeleteStep2Response] = useState<DevicesStepResponse>();
  const [selectedPassKeyForDelete, setSelectedPassKeyForDelete] = useState<typeof passKey>();
  const showDialog = (device: PassKey) => {
    setAddPassKeySuccess(false);
    setAddedPassKeyMessage("");
    setIsDialogOpen(true);
    const deviceMessage = device.key;
    setSelectedPassKeyForDelete([device]);
    setDialogDeviceMessage(device.key);
    setDeviceToRemove(device.value);
    setLastRemovedDeviceMessage(deviceMessage);
  };
  const onRemoveDevice = () => {
    setAddPassKeySuccess(false);
    setAddedPassKeyMessage("");
    setIsDialogOpen(false);
    setIsDeleting(true);
    setIsLoading(true);
    fetchData(deviceBackendUrl, "", configPassKey, setPassKeyStep1Response, passKeyFetchFail);
  };
  const onCloseDialog = () => {
    setIsDialogOpen(false);
    setDeviceToRemove("");
    setLastRemovedDeviceMessage("");
  };

  const deviceBackendUrl = cidmUrl + devicesUrl;

  const fetchData = (
    url: string,
    data: any,
    config: any,
    setResponse: React.Dispatch<React.SetStateAction<DevicesStepResponse | undefined>>,
    errorType: string
  ) => {
    axios
      .post<DevicesStepResponse>(url, data, config)
      .then(data => {
        setResponse(data.data);
      })
      .catch(error => {
        setIsError(errorType);
        setIsLoading(false);
      });
  };

  const configPassKey = useMemo(() => {
    return {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
        "accept-api-version": "protocol=1.0,resource=2.1",
        "Content-Type": "application/json",
      },
      withCredentials: true,
      params: {
        authIndexType: "composite_advice",
        authIndexValue:
          "<Advices><AttributeValuePair><Attribute name=\"AuthenticateToServiceConditionAdvice\"/><Value>/members:HCSC_Manage_WebAuthN_Device</Value></AttributeValuePair></Advices>",
      },
    };
  }, [accessToken]);
  //initial cidm call to fetch list of devices on pageload - Step 1
  useEffect(() => {
    if (accessToken && cidmUrl) {
      setIsLoading(true);
      fetchData(deviceBackendUrl, "", configPassKey, setPassKeyStep1Response, passKeyFetchFail);
    }
  }, [cidmUrl, accessToken]);

  useEffect(() => {
    if (!passKeyStep1Response || passKeyStep1Response?.callbacks == undefined) return;
    if (isDeleting) {
      const callback = passKeyStep1Response?.callbacks;
      if (callback) {
        callback[0].input[0].value = 1;
      }
      fetchData(
        deviceBackendUrl,
        { ...passKeyStep1Response, callbacks: callback },
        configPassKey,
        setPassKeyDeleteResponse,
        passKeyAddorDeleteFail
      );
    } else {
      const callback = passKeyStep1Response?.callbacks;
      if (callback) {
        callback[0].input[0].value = 2;
      }
      fetchData(
        deviceBackendUrl,
        { ...passKeyStep1Response, callbacks: callback },
        configPassKey,
        setPassKeyStep2Response,
        passKeyFetchFail
      );
    }
  }, [passKeyStep1Response]);

  useEffect(() => {
    if (!passKeyStep2Response) return;
    //saving fetched devices in state
    if (passKeyStep2Response) {
      const passKeyArray = passKeyStep2Response.callbacks[0]?.output[1]?.value;
      if (Array.isArray(passKeyArray) && passKeyArray[0] !== " ") {
        const parsedArray: PassKey[] = passKeyArray.map(choice => {
          const [key, value] = choice.split(" : ");
          return { key: key?.trim(), value: value?.trim() };
        });
        setPassKey(parsedArray);
        setPassKeyCount(parsedArray.length);
      } else if (Array.isArray(passKeyArray) && passKeyArray[0] == " ") {
        setPassKey([]);
        setPassKeyCount(0);
      } else {
        setIsError(passKeyFetchFail);
      }
      setIsLoading(false);
    }
  }, [passKeyStep2Response]);

  useEffect(() => {
    if (!passKeyDeleteResponse) return;
    else {
      let devicesArray;
      if (
        passKeyDeleteResponse.callbacks[0]?.output[0]?.value === "enter device Id" &&
        Array.isArray((devicesArray = passKeyDeleteResponse.callbacks[0]?.output[1]?.value))
      ) {
        const passKeyArray = passKeyDeleteResponse.callbacks[0]?.output[1]?.value;
        let passKeyResponse: PassKey[] = [];
        if (Array.isArray(passKeyArray) && passKeyArray[0] !== " ") {
          passKeyResponse = passKeyArray.map(choice => {
            const [key, value] = choice.split(" : ");
            return { key: key?.trim(), value: value?.trim() };
          });
        }
        const deleteDeviceIndex = passKeyResponse.findIndex(item => item.value === deviceToRemove);
        if (deleteDeviceIndex > -1) {
          const callback = passKeyDeleteResponse.callbacks;
          callback[0].input[0].value = deleteDeviceIndex;
          fetchData(
            deviceBackendUrl,
            { ...passKeyDeleteResponse, callbacks: callback },
            configPassKey,
            setPassKeyDeleteStep2Response,
            passKeyAddorDeleteFail
          );
        }
      } else {
        setIsError(passKeyAddorDeleteFail);
        setIsLoading(false);
      }
    }
  }, [passKeyDeleteResponse]);

  useEffect(() => {
    if (!passKeyDeleteStep2Response) return;
    else if (passKeyDeleteStep2Response?.callbacks[0]?.output[0]?.value === "Success! Do you want to continue?") {
      setRemoveDeviceSuccess(true);
      setIsDeleting(false);
      setDeviceToRemove("");
      setLastRemovedDeviceMessage(selectedPassKeyForDelete?.[0]?.key ?? "");
      //fetching new device list again
      fetchData(deviceBackendUrl, "", configPassKey, setPassKeyStep1Response, trustedDevicesFetchFail);
    }
  }, [passKeyDeleteStep2Response]);

  return (
    <>
      {passKey.map(device => (
        <DeviceCard onRemovePassKey={showDialog} passKey={device} key={device.value} closeAlert={closeAlert} />
      ))}
      {isDialogOpen && (
        <RemoveDeviceDialog
          dialogDeviceMessage={dialogDeviceMessage}
          isOpen={isDialogOpen}
          onClose={onCloseDialog}
          onSubmit={onRemoveDevice}
          type="passkey"
        />
      )}
    </>
  );
};
